/**
 * Byte Visualizer Component
 *
 * Visual representation of binary data with hex and decimal views.
 * Highlights byte ranges mapped to fields and supports click-to-create-field.
 */

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { useBinaryFormatStore } from '@/stores/binary-formats/binary-format-store';
import { getDataTypeBytes, dataTypeRequiresLength } from '@/types/binaryFormat';
import type { BinaryField } from '@/types/binaryFormat';

// Color palette for field highlighting (based on field index)
const FIELD_COLORS = [
  'bg-blue-200 dark:bg-blue-800',
  'bg-green-200 dark:bg-green-800',
  'bg-purple-200 dark:bg-purple-800',
  'bg-orange-200 dark:bg-orange-800',
  'bg-pink-200 dark:bg-pink-800',
  'bg-teal-200 dark:bg-teal-800',
  'bg-yellow-200 dark:bg-yellow-800',
  'bg-red-200 dark:bg-red-800',
];

export const ByteVisualizer: React.FC = () => {
  const { currentFormat, sampleData, setSampleData, addField } =
    useBinaryFormatStore();

  const [isExpanded, setIsExpanded] = useState(true);
  const [showDecimal, setShowDecimal] = useState(true);

  // Decode sample data to bytes
  const bytes = useMemo(() => {
    if (!sampleData || !currentFormat?.inputFormat) {
      return [];
    }

    try {
      return decodeToBytes(sampleData, currentFormat.inputFormat);
    } catch {
      return [];
    }
  }, [sampleData, currentFormat?.inputFormat]);

  // Build a map of byte index to field info
  const byteFieldMap = useMemo(() => {
    const map = new Map<
      number,
      { field: BinaryField; fieldIndex: number; byteIndexInField: number }
    >();

    if (!currentFormat?.fields) return map;

    currentFormat.fields.forEach((field, fieldIndex) => {
      const size = getFieldByteSize(field);
      for (let i = 0; i < size; i++) {
        map.set(field.offset + i, {
          field,
          fieldIndex,
          byteIndexInField: i,
        });
      }
    });

    return map;
  }, [currentFormat?.fields]);

  const handleByteClick = (byteIndex: number) => {
    // Check if byte is already mapped
    if (byteFieldMap.has(byteIndex)) {
      return;
    }
    // Add a new field at this offset
    addField({ offset: byteIndex, name: `field_${byteIndex}`, type: 'uint8' });
  };

  if (!currentFormat) {
    return null;
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="border rounded-md mb-3">
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-2 cursor-pointer hover:bg-muted/50">
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <span className="text-sm font-medium">Byte Visualizer</span>
              {bytes.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {bytes.length} bytes
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDecimal(!showDecimal);
                }}
              >
                {showDecimal ? (
                  <Eye className="h-3 w-3 mr-1" />
                ) : (
                  <EyeOff className="h-3 w-3 mr-1" />
                )}
                <span className="text-xs">{showDecimal ? 'Hex+Dec' : 'Hex'}</span>
              </Button>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-3 pt-0 space-y-3">
            {/* Sample Data Input */}
            <div className="space-y-1">
              <Label className="text-xs">Sample Data ({currentFormat.inputFormat})</Label>
              <Input
                value={sampleData}
                onChange={(e) => setSampleData(e.target.value)}
                placeholder={getPlaceholder(currentFormat.inputFormat)}
                className="font-mono text-xs"
              />
            </div>

            {/* Byte Grid */}
            {bytes.length > 0 ? (
              <div className="space-y-2">
                {/* Header Row */}
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <div className="w-10 text-right font-mono">Off</div>
                  {Array.from({ length: Math.min(16, bytes.length) }).map((_, i) => (
                    <div key={i} className="w-8 text-center font-mono">
                      +{i.toString(16).toUpperCase()}
                    </div>
                  ))}
                </div>

                {/* Byte Rows (16 bytes per row) */}
                <TooltipProvider delayDuration={100}>
                  <div className="space-y-1">
                    {chunkArray(bytes, 16).map((row, rowIndex) => (
                      <div key={rowIndex} className="flex items-center gap-1">
                        {/* Row Offset */}
                        <div className="w-10 text-right font-mono text-xs text-muted-foreground">
                          {(rowIndex * 16).toString(16).padStart(4, '0').toUpperCase()}
                        </div>

                        {/* Bytes */}
                        {row.map((byte, colIndex) => {
                          const byteIndex = rowIndex * 16 + colIndex;
                          const fieldInfo = byteFieldMap.get(byteIndex);
                          const colorClass = fieldInfo
                            ? FIELD_COLORS[fieldInfo.fieldIndex % FIELD_COLORS.length]
                            : '';

                          return (
                            <Tooltip key={colIndex}>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className={`w-8 text-center font-mono text-xs py-0.5 rounded transition-colors ${
                                    fieldInfo
                                      ? `${colorClass} cursor-default`
                                      : 'hover:bg-muted cursor-pointer'
                                  }`}
                                  onClick={() => handleByteClick(byteIndex)}
                                >
                                  <div>{byte.toString(16).padStart(2, '0').toUpperCase()}</div>
                                  {showDecimal && (
                                    <div className="text-[10px] text-muted-foreground">
                                      {byte}
                                    </div>
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                <div>Offset: {byteIndex} (0x{byteIndex.toString(16).toUpperCase()})</div>
                                <div>Value: {byte} (0x{byte.toString(16).toUpperCase().padStart(2, '0')})</div>
                                {fieldInfo ? (
                                  <div className="mt-1 font-medium text-primary">
                                    Field: {fieldInfo.field.name || '(unnamed)'}
                                    {getFieldByteSize(fieldInfo.field) > 1 &&
                                      ` [byte ${fieldInfo.byteIndexInField + 1}/${getFieldByteSize(fieldInfo.field)}]`}
                                  </div>
                                ) : (
                                  <div className="mt-1 text-muted-foreground">
                                    Click to create field
                                  </div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </TooltipProvider>

                {/* Field Legend */}
                {currentFormat.fields && currentFormat.fields.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    {currentFormat.fields.map((field, index) => (
                      <div
                        key={field.id}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                          FIELD_COLORS[index % FIELD_COLORS.length]
                        }`}
                      >
                        <span className="font-mono">@{field.offset}</span>
                        <span>{field.name || '(unnamed)'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : sampleData ? (
              <div className="text-sm text-destructive">
                Invalid {currentFormat.inputFormat} data
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Enter sample data above to visualize bytes
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ================================
// Utility Functions
// ================================

/**
 * Decode input string to byte array based on format
 */
function decodeToBytes(
  data: string,
  format: 'base64' | 'hex' | 'binary' | 'raw'
): number[] {
  switch (format) {
    case 'base64': {
      const binary = atob(data);
      return Array.from(binary, (char) => char.charCodeAt(0));
    }
    case 'hex': {
      const cleaned = data.replace(/[\s-:]/g, '').toLowerCase();
      if (cleaned.length % 2 !== 0) {
        throw new Error('Invalid hex string length');
      }
      const bytes: number[] = [];
      for (let i = 0; i < cleaned.length; i += 2) {
        const byte = parseInt(cleaned.substring(i, i + 2), 16);
        if (isNaN(byte)) {
          throw new Error('Invalid hex character');
        }
        bytes.push(byte);
      }
      return bytes;
    }
    case 'binary': {
      const cleaned = data.replace(/\s/g, '');
      if (cleaned.length % 8 !== 0) {
        throw new Error('Invalid binary string length');
      }
      const bytes: number[] = [];
      for (let i = 0; i < cleaned.length; i += 8) {
        const byte = parseInt(cleaned.substring(i, i + 8), 2);
        if (isNaN(byte)) {
          throw new Error('Invalid binary character');
        }
        bytes.push(byte);
      }
      return bytes;
    }
    case 'raw':
      return Array.from(data, (char) => char.charCodeAt(0));
    default:
      return [];
  }
}

/**
 * Get the byte size for a field
 */
function getFieldByteSize(field: BinaryField): number {
  // For variable-length types, use the length property
  if (dataTypeRequiresLength(field.type)) {
    return field.length || 1;
  }

  // For bits type, calculate bytes needed
  if (field.type === 'bits') {
    const bitLength = field.bitLength || 1;
    return Math.ceil(bitLength / 8);
  }

  // For fixed-size types
  const bytes = getDataTypeBytes(field.type);
  return bytes || 1;
}

/**
 * Get placeholder text for sample data input
 */
function getPlaceholder(format: string): string {
  switch (format) {
    case 'base64':
      return 'e.g., SGVsbG8gV29ybGQ=';
    case 'hex':
      return 'e.g., 48 65 6C 6C 6F or 48656C6C6F';
    case 'binary':
      return 'e.g., 01001000 01100101';
    case 'raw':
      return 'Raw bytes as string';
    default:
      return '';
  }
}

/**
 * Split array into chunks
 */
function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

export default ByteVisualizer;
